
import { useSelector } from "react-redux";

import { RootState } from "../Store/store";
import { capitalizeFirstLetter } from "../Library/Utils";

import textResource from "../Resources/Languages/Language.json";
import De from "../Resources/Languages/de.json";
import En from "../Resources/Languages/en.json";


const useLanguage = () => {
  // Redux generalSettingsReducer.language is the reactive source, so every
  // consumer of this hook re-renders on a language switch instead of relying
  // on a full page reload. This used to read localStorage directly instead
  // (see git history on this file) because Redux could go stale: a company-
  // settings fetch used to dispatch setLanguage(company_default), clobbering
  // the user's own choice, and the fallback order mattered on a 401 reset.
  // Both are gone now — the company-settings dispatch is commented out in
  // useFetchGeneralSettings, and useLogout's clearSession never touches this
  // slice — so the only writers left are the three places the user actually
  // confirms a language (HeaderComponent, ResetPassword, InvitedSignUp), all
  // of which already keep localStorage and Redux in sync. The slice's
  // initialState itself is seeded from localStorage at module load (see
  // `defaultLanguage` in Resources/Statics), so a fresh page load is correct
  // from the first render without needing a separate bootstrap dispatch.
  //
  // Anything that is not exactly "De" or "En" falls back to the default
  // rather than being used as a lookup suffix. A stray value (the literal
  // "null" a previous logout could leave behind, or a lowercase "de") used to
  // fall through to the legacy Language.json branch and resolve to undefined,
  // rendering blank labels across the whole UI.
  const generalLanguage = useSelector(
    (state: RootState) => state.generalSettingsReducer.language
  );
  const storedLanguage = capitalizeFirstLetter(String(generalLanguage ?? ""));
  const language = storedLanguage === "En" ? "En" : "De";

  const convertText = (key: string) => {
    if (capitalizeFirstLetter(language) === "De") return De[`${key}` as keyof typeof De];
    if (capitalizeFirstLetter(language) === "En") return En[`${key}` as keyof typeof En];

    return textResource[`${key}${language}` as keyof typeof textResource];
  };

  return {
    convertText};
};

export default useLanguage;
